import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { getClassLeaderboard, getSchoolLeaderboard } from '../lib/api';
import { toast } from 'sonner';
import { 
  ArrowLeft, Trophy, Zap, Target, BookOpen, Crown,
  Medal, Award, Star, TrendingUp
} from 'lucide-react';

const Leaderboard = () => {
  const navigate = useNavigate();
  const { classroomId } = useParams();
  const [activeTab, setActiveTab] = useState('typing_speed');
  const [scope, setScope] = useState(classroomId ? 'class' : 'school');
  const [classData, setClassData] = useState(null);
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLeaderboards = async () => {
      setLoading(true);
      try {
        if (classroomId) {
          const res = await getClassLeaderboard(classroomId);
          setClassData(res.data);
        }
        // Always fetch school data for comparison
        const schoolRes = await getSchoolLeaderboard();
        setSchoolData(schoolRes.data);
      } catch (error) {
        console.error('Failed to fetch leaderboards:', error);
        toast.error('Failed to load leaderboards');
      } finally {
        setLoading(false);
      }
    };

    fetchLeaderboards();
  }, [classroomId]);

  const currentData = scope === 'class' ? classData : schoolData;

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-5 h-5 text-neon-yellow" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-gray-300" />;
    if (rank === 3) return <Award className="w-5 h-5 text-amber-600" />;
    return <span className="w-5 h-5 flex items-center justify-center text-text-muted font-mono">{rank}</span>;
  };

  const getRankStyle = (rank) => {
    if (rank === 1) return 'bg-neon-yellow/10 border-neon-yellow/50';
    if (rank === 2) return 'bg-gray-300/10 border-gray-300/30';
    if (rank === 3) return 'bg-amber-600/10 border-amber-600/30';
    return 'bg-black/20 border-white/5';
  };

  const getMetricColor = (value, type) => {
    if (type === 'wpm') {
      if (value >= 60) return 'text-neon-green';
      if (value >= 40) return 'text-neon-yellow';
      return 'text-secondary';
    }
    // For percentages
    if (value >= 90) return 'text-neon-green';
    if (value >= 70) return 'text-neon-yellow';
    if (value >= 50) return 'text-neon-orange';
    return 'text-neon-red';
  };

  const renderLeaderboardTable = (data, type) => {
    if (!data || data.length === 0) {
      return (
        <div className="text-center py-12">
          <Trophy className="w-12 h-12 text-text-muted mx-auto mb-4 opacity-50" />
          <p className="text-text-secondary">No data yet</p>
          <p className="text-text-muted text-sm">Complete exercises to appear on the leaderboard!</p>
        </div>
      );
    }

    const metricKey = type === 'typing_speed' ? 'avg_wpm' : 
                      type === 'accuracy' ? 'avg_accuracy' :
                      type === 'quiz_score' ? 'avg_quiz_score' : 'avg_final_score';
    
    const weightedKey = type === 'typing_speed' ? 'weighted_wpm' : 
                        type === 'accuracy' ? 'weighted_accuracy' :
                        type === 'quiz_score' ? 'weighted_quiz_score' : 'weighted_final_score';

    const unit = type === 'typing_speed' ? ' WPM' : '%';
    const metricType = type === 'typing_speed' ? 'wpm' : 'percent';

    return (
      <div className="space-y-2">
        {data.map((student, index) => (
          <div 
            key={student.student_id}
            className={`flex items-center gap-4 p-4 rounded-lg border ${getRankStyle(index + 1)}`}
          >
            {/* Rank */}
            <div className="w-10 flex justify-center">
              {getRankIcon(index + 1)}
            </div>
            
            {/* Student Info */}
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{student.student_name}</p>
              <p className="text-text-muted text-xs">
                {student.exercises_completed || student.sessions_completed}/{student.total_exercises || '?'} exercises ({student.participation_rate || 0}%)
              </p>
            </div>
            
            {/* Participation Rate Badge */}
            <div className="text-center hidden sm:block">
              <p className="text-text-muted text-xs">Participation</p>
              <p className={`font-mono font-bold ${
                (student.participation_rate || 0) >= 80 ? 'text-neon-green' : 
                (student.participation_rate || 0) >= 50 ? 'text-neon-yellow' : 'text-neon-orange'
              }`}>
                {student.participation_rate || 0}%
              </p>
            </div>
            
            {/* Average Score */}
            <div className="text-center">
              <p className="text-text-muted text-xs">Average</p>
              <p className={`font-mono text-xl font-bold ${getMetricColor(student[metricKey], metricType)}`}>
                {student[metricKey]}{unit}
              </p>
            </div>
            
            {/* Weighted Score (used for ranking) */}
            <div className="text-center hidden md:block">
              <p className="text-text-muted text-xs">Weighted</p>
              <p className={`font-mono font-bold text-primary`}>
                {student[weightedKey] || 0}{unit}
              </p>
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-text-secondary">Loading leaderboards...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-white/5 bg-background-paper/50 backdrop-blur-md sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(-1)}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-neon-yellow/20 flex items-center justify-center">
                <Trophy className="w-5 h-5 text-neon-yellow" />
              </div>
              <div>
                <h1 className="font-heading text-xl font-bold">Leaderboard</h1>
                <p className="text-text-secondary text-sm">
                  {scope === 'class' ? classData?.classroom?.name : 'School-Wide Rankings'}
                </p>
              </div>
            </div>
          </div>
          
          {/* Scope Toggle */}
          {classroomId && schoolData && (
            <div className="flex items-center gap-2 bg-black/30 rounded-full p-1">
              <button
                onClick={() => setScope('class')}
                className={`px-4 py-1.5 rounded-full text-sm transition-all ${
                  scope === 'class' 
                    ? 'bg-primary text-white' 
                    : 'text-text-muted hover:text-white'
                }`}
              >
                Class
              </button>
              <button
                onClick={() => setScope('school')}
                className={`px-4 py-1.5 rounded-full text-sm transition-all ${
                  scope === 'school' 
                    ? 'bg-primary text-white' 
                    : 'text-text-muted hover:text-white'
                }`}
              >
                School
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Stats Overview */}
      <div className="max-w-4xl mx-auto px-6 py-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="card-glass p-4 text-center">
            <Zap className="w-6 h-6 text-neon-yellow mx-auto mb-2" />
            <p className="text-text-muted text-xs">Top Speed</p>
            <p className="font-heading text-2xl font-bold text-neon-yellow">
              {currentData?.leaderboards?.typing_speed?.[0]?.avg_wpm || 0}
              <span className="text-sm font-normal text-text-muted"> WPM</span>
            </p>
          </Card>
          <Card className="card-glass p-4 text-center">
            <Target className="w-6 h-6 text-neon-green mx-auto mb-2" />
            <p className="text-text-muted text-xs">Top Accuracy</p>
            <p className="font-heading text-2xl font-bold text-neon-green">
              {currentData?.leaderboards?.accuracy?.[0]?.avg_accuracy || 0}%
            </p>
          </Card>
          <Card className="card-glass p-4 text-center">
            <BookOpen className="w-6 h-6 text-secondary mx-auto mb-2" />
            <p className="text-text-muted text-xs">Top Quiz</p>
            <p className="font-heading text-2xl font-bold text-secondary">
              {currentData?.leaderboards?.quiz_score?.[0]?.avg_quiz_score || 0}%
            </p>
          </Card>
          <Card className="card-glass p-4 text-center">
            <Star className="w-6 h-6 text-primary mx-auto mb-2" />
            <p className="text-text-muted text-xs">Students</p>
            <p className="font-heading text-2xl font-bold text-primary">
              {currentData?.total_students || 0}
            </p>
          </Card>
        </div>

        {/* Leaderboard Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-6">
            <TabsTrigger value="typing_speed" className="flex items-center gap-1 text-xs sm:text-sm" data-testid="speed-tab">
              <Zap className="w-4 h-4" />
              <span className="hidden sm:inline">Typing Speed</span>
              <span className="sm:hidden">Speed</span>
            </TabsTrigger>
            <TabsTrigger value="accuracy" className="flex items-center gap-1 text-xs sm:text-sm" data-testid="accuracy-tab">
              <Target className="w-4 h-4" />
              <span className="hidden sm:inline">Accuracy</span>
              <span className="sm:hidden">Acc</span>
            </TabsTrigger>
            <TabsTrigger value="quiz_score" className="flex items-center gap-1 text-xs sm:text-sm" data-testid="quiz-tab">
              <BookOpen className="w-4 h-4" />
              <span className="hidden sm:inline">Quiz Score</span>
              <span className="sm:hidden">Quiz</span>
            </TabsTrigger>
            <TabsTrigger value="overall" className="flex items-center gap-1 text-xs sm:text-sm" data-testid="overall-tab">
              <Trophy className="w-4 h-4" />
              <span className="hidden sm:inline">Overall</span>
              <span className="sm:hidden">All</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="typing_speed">
            <Card className="card-glass p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-neon-yellow/20 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-neon-yellow" />
                </div>
                <div>
                  <h2 className="font-heading text-lg font-bold">Typing Speed Champions</h2>
                  <p className="text-text-muted text-sm">Fastest average words per minute</p>
                </div>
              </div>
              {renderLeaderboardTable(currentData?.leaderboards?.typing_speed, 'typing_speed')}
            </Card>
          </TabsContent>

          <TabsContent value="accuracy">
            <Card className="card-glass p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-neon-green/20 flex items-center justify-center">
                  <Target className="w-5 h-5 text-neon-green" />
                </div>
                <div>
                  <h2 className="font-heading text-lg font-bold">Accuracy Masters</h2>
                  <p className="text-text-muted text-sm">Highest typing accuracy</p>
                </div>
              </div>
              {renderLeaderboardTable(currentData?.leaderboards?.accuracy, 'accuracy')}
            </Card>
          </TabsContent>

          <TabsContent value="quiz_score">
            <Card className="card-glass p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-secondary/20 flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-secondary" />
                </div>
                <div>
                  <h2 className="font-heading text-lg font-bold">Quiz Champions</h2>
                  <p className="text-text-muted text-sm">Best comprehension quiz scores</p>
                </div>
              </div>
              {renderLeaderboardTable(currentData?.leaderboards?.quiz_score, 'quiz_score')}
            </Card>
          </TabsContent>

          <TabsContent value="overall">
            <Card className="card-glass p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                  <Trophy className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h2 className="font-heading text-lg font-bold">Overall Leaders</h2>
                  <p className="text-text-muted text-sm">Highest combined final scores</p>
                </div>
              </div>
              {renderLeaderboardTable(currentData?.leaderboards?.overall, 'overall')}
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Leaderboard;
